import { config } from '../config/config';
import stocksList from '../data/stocksList.json';
import type { BalanceSheet, CashFlowStatement, IncomeStatement, KeyMetrics } from './stockApi';

type FiscalMetric = {
  standardizedMetricId?: string;
  metricName?: string;
  metricFormat?: string;
};

type FiscalMetricValue = {
  value?: number;
  unit?: string;
  currency?: string;
  originalValue?: number;
  originalCurrency?: string;
};

type FiscalPeriodRow = {
  periodId?: string;
  periodType?: string;
  reportDate?: string;
  calendarYear?: number;
  calendarQuarter?: number;
  fiscalYear?: number;
  fiscalQuarter?: number;
  metricsValues?: Record<string, FiscalMetricValue>;
  metricValues?: Record<string, number>;
  currency?: string;
  originalCurrency?: string;
};

type FiscalStatementResponse = {
  metrics?: FiscalMetric[];
  data?: FiscalPeriodRow[];
  reportingCurrency?: string;
  currency?: string;
};

type FiscalRatiosResponse = {
  metrics?: FiscalMetric[];
  data?: FiscalPeriodRow[];
};

const FISCAL_DEFAULT_EXCHANGE = 'NASDAQ';

const AUTH_STORAGE_KEY = 'garp_admin_auth_v1';

let didWarnMissingFiscalProxy = false;
let didWarnMissingBackendProxy = false;
let didWarnMissingBackendBase = false;

function getBackendAuthTokenFromStorage(): string {
  try {
    if (typeof window === 'undefined') return '';
    const raw = window.localStorage?.getItem(AUTH_STORAGE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { token?: unknown; expiresAtUtc?: unknown };
    const token = typeof parsed?.token === 'string' ? parsed.token.trim() : '';
    if (!token) return '';

    const expiresAtUtc = typeof parsed?.expiresAtUtc === 'string' ? parsed.expiresAtUtc : '';
    const expiresMs = expiresAtUtc ? new Date(expiresAtUtc).getTime() : NaN;
    if (Number.isFinite(expiresMs) && expiresMs <= Date.now()) return '';

    return token;
  } catch {
    return '';
  }
}

function canUseBackendFiscalProxy(): boolean {
  if (!config.useRealAPI) return false;
  const base = (config.backend?.baseUrl ?? '').trim();
  if (!base) return false;
  return getBackendAuthTokenFromStorage().length > 0;
}

type FiscalGetRequest = { url: string; init?: RequestInit };

function makeFiscalGetRequest(
  path: string,
  query: Record<string, string | number | boolean | null | undefined>
): FiscalGetRequest | null {
  if (!config.useRealAPI) return null;

  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v == null) continue;
    if (String(k).trim().toLowerCase() === 'currency') continue;
    const sv = String(v).trim();
    if (!sv) continue;
    qs.set(k, sv);
  }

  // Enforce USD across all Fiscal calls from the client.
  qs.set('currency', 'USD');

  if (canUseBackendFiscalProxy()) {
    const token = getBackendAuthTokenFromStorage();
    const base = (config.backend?.baseUrl ?? '').trim().replace(/\/$/, '');
    const url = `${base}/fiscal${path}${qs.toString() ? `?${qs.toString()}` : ''}`;
    return {
      url,
      init: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    };
  }

  if (!didWarnMissingFiscalProxy) {
    didWarnMissingFiscalProxy = true;
    console.warn(
      'Fiscal requests are proxy-only for security. Please login so the app can call the backend /fiscal proxy (server-side key).'
    );
  }

  return null;
}

function makeBackendGetRequest(
  path: string,
  query: Record<string, string | number | boolean | null | undefined>
): FiscalGetRequest | null {
  if (!config.useRealAPI) return null;
  const base = (config.backend?.baseUrl ?? '').trim();
  if (!base) return null;
  const token = getBackendAuthTokenFromStorage();
  if (!token) {
    if (!didWarnMissingBackendProxy) {
      didWarnMissingBackendProxy = true;
      console.warn('Backend requests require login. Please login and try again.');
    }
    return null;
  }

  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v == null) continue;
    const sv = String(v).trim();
    if (!sv) continue;
    qs.set(k, sv);
  }

  const baseUrl = base.replace(/\/$/, '');
  const url = `${baseUrl}${path}${qs.toString() ? `?${qs.toString()}` : ''}`;
  return {
    url,
    init: {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  };
}

function makeBackendPublicGetRequest(
  path: string,
  query: Record<string, string | number | boolean | null | undefined>
): FiscalGetRequest | null {
  if (!config.useRealAPI) return null;
  const base = (config.backend?.baseUrl ?? '').trim();
  if (!base) {
    if (!didWarnMissingBackendBase) {
      didWarnMissingBackendBase = true;
      console.warn('Backend base URL is missing; cannot call public backend endpoints.');
    }
    return null;
  }

  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v == null) continue;
    const sv = String(v).trim();
    if (!sv) continue;
    qs.set(k, sv);
  }

  const baseUrl = base.replace(/\/$/, '');
  const url = `${baseUrl}${path}${qs.toString() ? `?${qs.toString()}` : ''}`;
  return { url };
}

const normalizeMetricName = (value?: string) => (value ?? '').trim().toLowerCase();

const pickMetricId = (metrics: FiscalMetric[] | undefined, candidates: Array<{ id?: string; name?: string }>) => {
  if (!metrics || metrics.length === 0) return undefined;

  for (const candidate of candidates) {
    if (candidate.id) {
      const hit = metrics.find((m) => (m.standardizedMetricId ?? '').toLowerCase() === candidate.id?.toLowerCase());
      if (hit?.standardizedMetricId) return hit.standardizedMetricId;
    }
  }

  for (const candidate of candidates) {
    if (!candidate.name) continue;
    const target = candidate.name.toLowerCase();
    const hit = metrics.find((m) => normalizeMetricName(m.metricName) === target);
    if (hit?.standardizedMetricId) return hit.standardizedMetricId;
  }

  for (const candidate of candidates) {
    if (!candidate.name) continue;
    const target = candidate.name.toLowerCase();
    const hit = metrics.find((m) => normalizeMetricName(m.metricName).includes(target));
    if (hit?.standardizedMetricId) return hit.standardizedMetricId;
  }

  return undefined;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const normalized = trimmed.replace(/,/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const emptyIncomeStatement = (overrides: Partial<IncomeStatement>): IncomeStatement => ({
  date: '',
  symbol: '',
  reportedCurrency: 'USD',
  cik: '',
  filingDate: '',
  acceptedDate: '',
  fiscalYear: '',
  period: '',
  revenue: 0,
  costOfRevenue: 0,
  grossProfit: 0,
  researchAndDevelopmentExpenses: 0,
  generalAndAdministrativeExpenses: 0,
  sellingAndMarketingExpenses: 0,
  sellingGeneralAndAdministrativeExpenses: 0,
  otherExpenses: 0,
  operatingExpenses: 0,
  costAndExpenses: 0,
  netInterestIncome: 0,
  interestIncome: 0,
  interestExpense: 0,
  depreciationAndAmortization: 0,
  ebitda: 0,
  ebit: 0,
  nonOperatingIncomeExcludingInterest: 0,
  operatingIncome: 0,
  totalOtherIncomeExpensesNet: 0,
  incomeBeforeTax: 0,
  incomeTaxExpense: 0,
  netIncomeFromContinuingOperations: 0,
  netIncomeFromDiscontinuedOperations: 0,
  otherAdjustmentsToNetIncome: 0,
  netIncome: 0,
  netIncomeDeductions: 0,
  bottomLineNetIncome: 0,
  eps: 0,
  epsDiluted: 0,
  weightedAverageShsOut: 0,
  weightedAverageShsOutDil: 0,
  ...overrides,
});

const emptyCashFlowStatement = (overrides: Partial<CashFlowStatement>): CashFlowStatement => ({
  date: '',
  symbol: '',
  reportedCurrency: 'USD',
  cik: '',
  filingDate: '',
  acceptedDate: '',
  fiscalYear: '',
  period: '',
  netIncome: 0,
  depreciationAndAmortization: 0,
  deferredIncomeTax: 0,
  stockBasedCompensation: 0,
  changeInWorkingCapital: 0,
  accountsReceivables: 0,
  inventory: 0,
  accountsPayables: 0,
  otherWorkingCapital: 0,
  otherNonCashItems: 0,
  netCashProvidedByOperatingActivities: 0,
  investmentsInPropertyPlantAndEquipment: 0,
  acquisitionsNet: 0,
  purchasesOfInvestments: 0,
  salesMaturitiesOfInvestments: 0,
  otherInvestingActivities: 0,
  netCashUsedForInvestingActivities: 0,
  debtRepayment: 0,
  commonStockIssued: 0,
  commonStockRepurchased: 0,
  dividendsPaid: 0,
  netDividendsPaid: 0,
  commonDividendsPaid: 0,
  otherFinancingActivities: 0,
  netCashUsedProvidedByFinancingActivities: 0,
  effectOfForexChangesOnCash: 0,
  netChangeInCash: 0,
  cashAtEndOfPeriod: 0,
  cashAtBeginningOfPeriod: 0,
  operatingCashFlow: 0,
  capitalExpenditure: 0,
  freeCashFlow: 0,
  ...overrides,
});

const emptyBalanceSheet = (overrides: Partial<BalanceSheet>): BalanceSheet => ({
  date: '',
  symbol: '',
  reportedCurrency: 'USD',
  cik: '',
  filingDate: '',
  acceptedDate: '',
  fiscalYear: '',
  period: '',
  cashAndCashEquivalents: Number.NaN,
  shortTermInvestments: Number.NaN,
  cashAndShortTermInvestments: Number.NaN,
  netReceivables: Number.NaN,
  inventory: Number.NaN,
  otherCurrentAssets: Number.NaN,
  totalCurrentAssets: Number.NaN,
  propertyPlantEquipmentNet: Number.NaN,
  goodwill: Number.NaN,
  intangibleAssets: Number.NaN,
  goodwillAndIntangibleAssets: Number.NaN,
  longTermInvestments: Number.NaN,
  taxAssets: Number.NaN,
  otherNonCurrentAssets: Number.NaN,
  totalNonCurrentAssets: Number.NaN,
  otherAssets: Number.NaN,
  totalAssets: Number.NaN,
  accountPayables: Number.NaN,
  otherPayables: Number.NaN,
  accruedExpenses: Number.NaN,
  shortTermDebt: Number.NaN,
  taxPayables: Number.NaN,
  deferredRevenue: Number.NaN,
  otherCurrentLiabilities: Number.NaN,
  totalCurrentLiabilities: Number.NaN,
  longTermDebt: Number.NaN,
  capitalLeaseObligationsNonCurrent: Number.NaN,
  deferredRevenueNonCurrent: Number.NaN,
  deferredTaxLiabilitiesNonCurrent: Number.NaN,
  otherNonCurrentLiabilities: Number.NaN,
  totalNonCurrentLiabilities: Number.NaN,
  otherLiabilities: Number.NaN,
  capitalLeaseObligations: Number.NaN,
  totalLiabilities: Number.NaN,
  treasuryStock: Number.NaN,
  preferredStock: Number.NaN,
  commonStock: Number.NaN,
  retainedEarnings: Number.NaN,
  additionalPaidInCapital: Number.NaN,
  accumulatedOtherComprehensiveIncomeLoss: Number.NaN,
  otherTotalStockholdersEquity: Number.NaN,
  othertotalStockholdersEquity: Number.NaN,
  totalStockholdersEquity: Number.NaN,
  totalEquity: Number.NaN,
  totalLiabilitiesAndStockholdersEquity: Number.NaN,
  minorityInterest: Number.NaN,
  totalLiabilitiesAndTotalEquity: Number.NaN,
  totalInvestments: Number.NaN,
  totalDebt: Number.NaN,
  netDebt: Number.NaN,
  ...overrides,
});

type BalanceSheetNumberKey = {
  [K in keyof BalanceSheet]-?: BalanceSheet[K] extends number | undefined ? K : never;
}[keyof BalanceSheet];

const mapPeriodType = (period: 'annual' | 'quarter') => (period === 'annual' ? 'annual' : 'quarterly');

const buildCompanyKey = (symbol: string, exchange: string) => `${exchange}_${symbol}`;

const extractMetricValue = (row: FiscalPeriodRow, metricId?: string) => {
  if (!metricId) return undefined;
  const metricsValues = row.metricsValues ?? {};
  const metricValue = metricsValues[metricId];
  if (!metricValue) return undefined;
  return toNumber(metricValue.value ?? metricValue.originalValue);
};

const extractRatioValue = (row: FiscalPeriodRow, keys: string[]) => {
  const values = row.metricValues ?? {};
  for (const key of keys) {
    if (key in values) {
      const v = toNumber(values[key]);
      if (typeof v === 'number') return v;
    }
  }
  return undefined;
};

const toIsoDate = (value?: string) => {
  const raw = (value ?? '').trim();
  return raw || '';
};

const resolveFiscalReportDate = (row: Partial<FiscalStandardizedDataRow> & Record<string, unknown>) => {
  const pick = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  return (
    pick(row.reportDate) ||
    pick((row as any).periodEndDate) ||
    pick((row as any).periodEnd) ||
    pick((row as any).fiscalPeriodEndDate) ||
    pick((row as any).fiscalPeriodEnd) ||
    ''
  );
};

const resolveFiscalPeriodEndDate = (row: Partial<FiscalStandardizedDataRow> & Record<string, unknown>) => {
  const pick = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  return (
    pick((row as any).periodEndDate) ||
    pick((row as any).periodEnd) ||
    pick((row as any).fiscalPeriodEndDate) ||
    pick((row as any).fiscalPeriodEnd) ||
    pick(row.reportDate) ||
    ''
  );
};

const resolveFiscalFilingDate = (row: Partial<FiscalStandardizedDataRow> & Record<string, unknown>) => {
  const pick = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  return (
    pick((row as any).lastSourceFilingDate) ||
    pick((row as any).last_source_filing_date) ||
    pick((row as any).sourceFilingDate) ||
    pick((row as any).source_filing_date) ||
    pick((row as any).filingDate) ||
    pick((row as any).filing_date) ||
    pick((row as any).secFilingDate) ||
    pick((row as any).sec_filing_date) ||
    ''
  );
};

const resolveFiscalEarningsDate = (row: Partial<FiscalStandardizedDataRow> & Record<string, unknown>) => {
  const pick = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  return (
    pick((row as any).earningDate) ||
    pick((row as any).earningsDate) ||
    pick(row.reportDate) ||
    pick((row as any).filingDate) ||
    ''
  );
};

export async function fetchFiscalIncomeStatement(
  symbol: string,
  period: 'annual' | 'quarter',
  limit: number
): Promise<IncomeStatement[]> {
  const companyKey = buildCompanyKey(symbol, FISCAL_DEFAULT_EXCHANGE);
  const request = makeFiscalGetRequest('/v1/company/financials/income-statement/standardized', {
    companyKey,
    periodType: mapPeriodType(period),
  });

  if (!request) return [];

  const response = await fetch(request.url, request.init);
  if (!response.ok) return [];

  const payload = (await response.json()) as FiscalStatementResponse;
  const metrics = payload.metrics ?? [];
  const data = payload.data ?? [];

  const revenueId = pickMetricId(metrics, [
    { id: 'income_statement_total_revenues' },
    { id: 'income_statement_total_revenue' },
    { id: 'income_statement_revenue' },
    { name: 'Total Revenues' },
    { name: 'Total Revenue' },
    { name: 'Net Revenue' },
    { name: 'Revenue' },
    { name: 'Net Sales' },
    { name: 'Sales' },
  ]);
  const grossProfitId = pickMetricId(metrics, [
    { id: 'income_statement_gross_profit' },
    { name: 'Gross Profit' },
  ]);
  const operatingIncomeId = pickMetricId(metrics, [
    { id: 'income_statement_operating_income' },
    { id: 'income_statement_income_from_operations' },
    { name: 'Operating Income' },
    { name: 'Income from Operations' },
  ]);
  const netIncomeId = pickMetricId(metrics, [
    { id: 'income_statement_net_income' },
    { id: 'income_statement_net_income_common_stockholders' },
    { name: 'Net Income' },
  ]);
  const epsId = pickMetricId(metrics, [
    { id: 'income_statement_eps_diluted' },
    { id: 'income_statement_eps_basic' },
    { id: 'income_statement_eps' },
    { name: 'Earnings Per Share' },
    { name: 'EPS' },
    { name: 'Diluted EPS' },
  ]);

  const rows = data
    .filter((row) => String(row.periodType ?? '').toLowerCase().includes(mapPeriodType(period)))
    .slice(0, limit)
    .map((row) => {
      const date = toIsoDate(row.reportDate);
      const fiscalYear = row.fiscalYear != null ? String(row.fiscalYear) : '';
      const reportedCurrency = row.currency ?? payload.reportingCurrency ?? payload.currency ?? 'USD';
      const revenue = extractMetricValue(row, revenueId) ?? 0;
      const grossProfit = extractMetricValue(row, grossProfitId) ?? 0;
      const operatingIncome = extractMetricValue(row, operatingIncomeId) ?? 0;
        const netIncome = extractMetricValue(row, netIncomeId) ?? 0;
      const eps = extractMetricValue(row, epsId) ?? 0;

      return emptyIncomeStatement({
        date,
        symbol,
        reportedCurrency,
        fiscalYear,
        period: period === 'annual' ? 'FY' : 'Q',
        revenue,
        grossProfit,
        operatingIncome,
        netIncome,
        eps,
        epsDiluted: eps,
      });
    })
    .filter((row) => row.date);

  return rows;
}

export async function fetchFiscalCashFlowStatement(
  symbol: string,
  period: 'annual' | 'quarter',
  limit: number
): Promise<CashFlowStatement[]> {
  const companyKey = buildCompanyKey(symbol, FISCAL_DEFAULT_EXCHANGE);
  const request = makeFiscalGetRequest('/v1/company/financials/cash-flow-statement/standardized', {
    companyKey,
    periodType: mapPeriodType(period),
  });

  if (!request) return [];

  const response = await fetch(request.url, request.init);
  if (!response.ok) return [];

  const payload = (await response.json()) as FiscalStatementResponse;
  const metrics = payload.metrics ?? [];
  const data = payload.data ?? [];

  const operatingCashFlowId = pickMetricId(metrics, [
    { id: 'cash_flow_net_cash_from_operating_activities' },
    { id: 'cash_flow_net_cash_provided_by_operating_activities' },
    { id: 'cash_flow_operating_cash_flow' },
    { name: 'Net Cash Provided by Operating Activities' },
    { name: 'Operating Cash Flow' },
    { name: 'Net Cash From Operating Activities' },
  ]);

  const rows = data
    .filter((row) => String(row.periodType ?? '').toLowerCase().includes(mapPeriodType(period)))
    .slice(0, limit)
    .map((row) => {
      const date = toIsoDate(row.reportDate);
      const fiscalYear = row.fiscalYear != null ? String(row.fiscalYear) : '';
      const reportedCurrency = row.currency ?? payload.reportingCurrency ?? payload.currency ?? 'USD';
      const operatingCashFlow = extractMetricValue(row, operatingCashFlowId) ?? 0;

      return emptyCashFlowStatement({
        date,
        symbol,
        reportedCurrency,
        fiscalYear,
        period: period === 'annual' ? 'FY' : 'Q',
        operatingCashFlow,
        netCashProvidedByOperatingActivities: operatingCashFlow,
      });
    })
    .filter((row) => row.date);

  return rows;
}

export async function fetchFiscalRatios(
  symbol: string,
  period: 'annual' | 'quarter',
  limit: number
): Promise<KeyMetrics[]> {
  const companyKey = buildCompanyKey(symbol, FISCAL_DEFAULT_EXCHANGE);
  const request = makeFiscalGetRequest('/v1/company/ratios', {
    companyKey,
    periodType: mapPeriodType(period),
  });

  if (!request) return [];

  const response = await fetch(request.url, request.init);
  if (!response.ok) return [];

  const payload = (await response.json()) as FiscalRatiosResponse;
  const data = payload.data ?? [];

  const rows = data
    .filter((row) => String(row.periodType ?? '').toLowerCase().includes(mapPeriodType(period)))
    .slice(0, limit)
    .map((row) => {
      const date = toIsoDate(row.reportDate);
      const roic = extractRatioValue(row, [
        'ratio_return_on_invested_capital',
        'return_on_invested_capital',
        'roic',
      ]);

      return {
        date,
        symbol,
        period: period === 'annual' ? 'annual' : 'quarter',
        roic: roic ?? 0,
        returnOnInvestedCapital: roic ?? 0,
        investedCapital: 0,
      } as KeyMetrics;
    })
    .filter((row) => row.date);

  return rows;
}

type FiscalStockPriceRow = {
  date?: string;
  open_price?: number;
  close_price?: number;
  high_price?: number;
  low_price?: number;
  volume?: number;
};

export type FiscalCompanyFilingRow = {
  filingId?: string;
  filing_id?: string;
  documentType?: string;
  document_type?: string;
  filingDate?: string;
  filing_date?: string;
  reportDate?: string;
  report_date?: string;
  fiscalYear?: number;
  fiscal_year?: number;
  pdfUrl?: string;
  pdf_url?: string;
};

export async function fetchFiscalStockPrices(symbol: string): Promise<FiscalStockPriceRow[]> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return [];

  const response = await fiscalGet<FiscalStockPriceRow[]>('/v2/company/stock-prices', {
    companyKey,
  });

  return Array.isArray(response) ? response : [];
}

export async function fetchFiscalCompanyFilings(symbol: string): Promise<FiscalCompanyFilingRow[]> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return [];

  const response = await fiscalGet<FiscalCompanyFilingRow[]>('/v2/company/filings', {
    companyKey,
  });

  return Array.isArray(response) ? response : [];
}

export async function fetchFiscalFilingPdfBlobUrl(symbol: string, filingId: string): Promise<string> {
  const companyKey = buildCompanyKeyFromList(symbol);
  const safeId = String(filingId || '').trim();
  if (!companyKey || !safeId) return '';

  const request = makeFiscalGetRequest(`/v1/filing/${encodeURIComponent(safeId)}/pdf`, {
    companyKey,
  });

  if (!request) return '';

  const response = await fetch(request.url, request.init);
  if (!response.ok) return '';

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

type SecFilingLookupResponse = {
  url?: string;
  accessionNumber?: string;
  reportDate?: string;
  form?: string;
  cik?: string;
};

export async function fetchSecFilingUrlForYear(
  symbol: string,
  year: number,
  form: string = '10-K'
): Promise<string> {
  if (!symbol || !Number.isFinite(year)) return '';
  const request = makeBackendPublicGetRequest('/sec/filing', { symbol, year, form });
  if (!request) return '';

  try {
    const response = await fetch(request.url, request.init);
    if (!response.ok) return '';
    const payload = (await response.json()) as SecFilingLookupResponse;
    return String(payload?.url ?? '').trim();
  } catch {
    return '';
  }
}

export async function fetchSecFilingUrlForReportDate(
  symbol: string,
  reportDate: string,
  form: string = '10-Q'
): Promise<string> {
  if (!symbol || !reportDate) return '';
  const year = new Date(reportDate).getFullYear();
  if (!Number.isFinite(year)) return '';

  const request = makeBackendPublicGetRequest('/sec/filing', {
    symbol,
    year,
    form,
    reportDate,
  });
  if (!request) return '';

  try {
    const response = await fetch(request.url, request.init);
    if (!response.ok) return '';
    const payload = (await response.json()) as SecFilingLookupResponse;
    return String(payload?.url ?? '').trim();
  } catch {
    return '';
  }
}

export type FiscalStandardizedMetric = {
  standardizedMetricId: string;
  metricName: string;
  statementType?: string;
  metricFormat?: string;
  isCurrency?: boolean;
  isPointInTime?: boolean;
};

type FiscalStandardizedMetricValue =
  | number
  | {
      value?: number;
      originalValue?: number;
      unit?: string;
      currency?: string;
      asReportedValues?: Array<{
        value?: number;
        unit?: string;
        currency?: string;
        sources?: Array<{
          document?: string;
          originalSourceUrl?: string;
        }>;
      }>;
    };

type FiscalAsReportedRow = {
  periodType?: string;
  reportDate?: string;
  filingDate?: string;
  filingId?: string;
  filing_id?: string;
  periodEndDate?: string;
  periodEnd?: string;
  fiscalPeriodEndDate?: string;
  fiscalPeriodEnd?: string;
  date?: string;
  metricsValues?: Record<string, FiscalAsReportedMetricValue>;
};

type FiscalAsReportedMetricValue = {
  value?: number | null;
  unit?: string;
  currency?: string;
  isCalculated?: boolean;
  sources?: Array<{
    document?: string;
    originalSourceUrl?: string;
  }>;
};

type FiscalAsReportedResponse = {
  data?: FiscalAsReportedRow[];
};

type FiscalStandardizedDataRow = {
  periodType?: string;
  reportDate?: string;
  filingDate?: string;
  filingId?: string;
  filing_id?: string;
  acceptedDate?: string;
  periodEndDate?: string;
  periodEnd?: string;
  fiscalPeriodEndDate?: string;
  fiscalPeriodEnd?: string;
  calendarYear?: number;
  calendarQuarter?: number;
  fiscalYear?: number;
  fiscalQuarter?: number;
  metricsValues?: Record<string, FiscalStandardizedMetricValue>;
};

type FiscalStandardizedResponse = {
  metrics?: FiscalStandardizedMetric[];
  data?: FiscalStandardizedDataRow[];
};

type FiscalRatioListItem = {
  ratioId: string;
  metricName?: string;
};

type FiscalCompanyRatioRow = {
  periodType?: string;
  reportDate?: string;
  metricValues?: Record<string, number>;
};

type FiscalCompanyRatiosResponse = {
  metrics?: FiscalRatioListItem[];
  data?: FiscalCompanyRatioRow[];
};

type FiscalCompanyProfileResponse = {
  description?: string;
  companyDescription?: string;
  businessDescription?: string;
  longDescription?: string;
  shortDescription?: string;
  overview?: string;
  profile?: {
    description?: string;
    longDescription?: string;
    overview?: string;
  };
  data?: {
    description?: string;
    companyDescription?: string;
    businessDescription?: string;
    longDescription?: string;
    shortDescription?: string;
    overview?: string;
  };
};

export type FiscalIncomeRow = {
  date: string;
  periodEndDate?: string;
  filingDate?: string;
  filingId?: string;
  earningDate?: string;
  calendarYear?: number;
  calendarQuarter?: number;
  fiscalYear?: number;
  fiscalQuarter?: number;
  revenue?: number;
  costOfRevenue?: number;
  grossProfit?: number;
  researchAndDevelopmentExpenses?: number;
  sellingGeneralAndAdministrativeExpenses?: number;
  interestIncome?: number;
  netInterestIncome?: number;
  interestExpense?: number;
  operatingIncome?: number;
  incomeBeforeTax?: number;
  incomeTaxExpense?: number;
  nonOperatingIncome?: number;
  operatingExpenses?: number;
  netIncome?: number;
  eps?: number;
  epsDiluted?: number;
  dilutedWeightedAverageSharesOutstanding?: number;
};

export type FiscalCashRow = {
  date: string;
  periodEndDate?: string;
  filingDate?: string;
  earningDate?: string;
  calendarYear?: number;
  calendarQuarter?: number;
  fiscalYear?: number;
  fiscalQuarter?: number;
  netIncome?: number;
  depreciationAndAmortization?: number;
  stockBasedCompensation?: number;
  changeInWorkingCapital?: number;
  accountsReceivables?: number;
  inventory?: number;
  accountsPayables?: number;
  otherWorkingCapital?: number;
  changesInIncomeTaxes?: number;
  changesInDeferredRevenue?: number;
  otherNonCashItems?: number;
  operatingCashFlow?: number;
  netCashProvidedByOperatingActivities?: number;
  investmentsInPropertyPlantAndEquipment?: number;
  purchasesOfInvestments?: number;
  salesMaturitiesOfInvestments?: number;
  acquisitionsNet?: number;
  otherInvestingActivities?: number;
  netCashUsedForInvestingActivities?: number;
  debtRepayment?: number;
  commonStockIssued?: number;
  commonStockRepurchased?: number;
  commonDividendsPaid?: number;
  dividendsPaid?: number;
  otherFinancingActivities?: number;
  netCashUsedProvidedByFinancingActivities?: number;
  effectOfForexChangesOnCash?: number;
  netChangeInCash?: number;
  capitalExpenditure?: number;
};

export type FiscalStatementResult<T> = {
  annual: T[];
  quarter: T[];
  error?: string;
};

export type FiscalTtmResult<T> = {
  row: T | null;
  date?: string;
  error?: string;
};

export type FiscalRatioRow = {
  date: string;
  periodType: 'annual' | 'quarter' | 'ltm' | 'other';
  value?: number;
};

let cachedStandardizedMetrics: FiscalStandardizedMetric[] | null = null;
let standardizedMetricsPromise: Promise<FiscalStandardizedMetric[]> | null = null;

function buildCompanyKeyFromList(symbol: string): string {
  const sym = String(symbol || '').trim().toUpperCase();
  if (!sym) return '';
  const stock = (stocksList as Array<{ symbol: string; exchange?: string }>).find(
    (s) => String(s.symbol || '').toUpperCase() === sym
  );
  const exchange = String(stock?.exchange || 'NASDAQ').trim().toUpperCase();
  return exchange ? `${exchange}_${sym}` : sym;
}

async function fiscalGet<T>(path: string, params: Record<string, string | number | undefined>): Promise<T | null> {
  const request = makeFiscalGetRequest(path, params);
  if (!request) return null;
  try {
    const response = await fetch(request.url, request.init);
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

async function fiscalGetWithError<T>(
  path: string,
  params: Record<string, string | number | undefined>
): Promise<{ data: T | null; error?: string }> {
  const buildCompanyKeyCandidates = (rawCompanyKey: string): string[] => {
    const normalized = String(rawCompanyKey || '').trim().toUpperCase();
    if (!normalized) return [];
    const underscoreIndex = normalized.indexOf('_');
    const parsedExchange = underscoreIndex > 0 ? normalized.slice(0, underscoreIndex).trim() : '';
    const parsedSymbol = underscoreIndex > 0 ? normalized.slice(underscoreIndex + 1).trim() : normalized;
    if (!parsedSymbol) return [normalized];

    const exchangesFromList = Array.from(
      new Set(
        (stocksList as Array<{ symbol?: string; exchange?: string }>)
          .filter((row) => String(row?.symbol || '').trim().toUpperCase() === parsedSymbol)
          .map((row) => String(row?.exchange || '').trim().toUpperCase())
          .filter((exchange) => exchange.length > 0)
      )
    );

    const commonExchangeFallbacks = [
      'NASDAQ',
      'NYSE',
      'AMEX',
      'OTC',
      'TSX',
      'LSE',
      'ASX',
      'HKEX',
      'XETRA',
    ];

    const candidates = [
      normalized,
      ...(parsedExchange ? [`${parsedExchange}_${parsedSymbol}`] : []),
      ...exchangesFromList.map((exchange) => `${exchange}_${parsedSymbol}`),
      ...commonExchangeFallbacks.map((exchange) => `${exchange}_${parsedSymbol}`),
    ];

    return Array.from(new Set(candidates));
  };

  const requestOnce = async (
    requestParams: Record<string, string | number | undefined>
  ): Promise<{ data: T | null; error?: string; status?: number }> => {
    const request = makeFiscalGetRequest(path, requestParams);
    if (!request) return { data: null, error: 'Missing backend proxy auth.' };
    try {
      const response = await fetch(request.url, request.init);
      if (!response.ok) {
        let message = `Fiscal API error (${response.status}).`;
        try {
          const payload = await response.json();
          const apiMessage = payload?.errors?.[0]?.message || payload?.message;
          if (apiMessage) message = String(apiMessage);
        } catch {
          // ignore
        }
        return { data: null, error: message, status: response.status };
      }
      const data = (await response.json()) as T;
      return { data, status: response.status };
    } catch (error: any) {
      return { data: null, error: error?.message || 'Fiscal API request failed.' };
    }
  };

  const primary = await requestOnce(params);
  if (primary.data) return { data: primary.data };

  const initialCompanyKey = String(params.companyKey || '').trim();
  const shouldTryCompanyKeyFallback = primary.status === 404 && initialCompanyKey.length > 0;
  if (!shouldTryCompanyKeyFallback) {
    return { data: null, error: primary.error };
  }

  const candidates = buildCompanyKeyCandidates(initialCompanyKey);
  for (const candidate of candidates) {
    if (!candidate || candidate === initialCompanyKey) continue;
    const result = await requestOnce({ ...params, companyKey: candidate });
    if (result.data) {
      // eslint-disable-next-line no-console
      console.warn(`Fiscal companyKey fallback succeeded: ${initialCompanyKey} -> ${candidate}`);
      return { data: result.data };
    }
    if (result.status && result.status !== 404) {
      return { data: null, error: result.error };
    }
  }

  return { data: null, error: primary.error };
}

function parseMetricNumber(value: unknown): number | undefined {
  const direct = toNumber(value);
  if (typeof direct === 'number') return direct;
  if (!value || typeof value !== 'object') return undefined;
  const maybeValue = (value as { value?: unknown }).value;
  const nested = toNumber(maybeValue);
  if (typeof nested === 'number') return nested;
  return undefined;
}

function parseOriginalMetricNumber(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') return parseMetricNumber(value);
  const maybeValue = (value as { originalValue?: unknown }).originalValue;
  if (typeof maybeValue === 'number' && Number.isFinite(maybeValue)) return maybeValue;
  return parseMetricNumber(value);
}

const parseFilingIdFromUrl = (value?: string): string | undefined => {
  const url = String(value ?? '').trim();
  if (!url) return undefined;
  const match = url.match(/\/filing\/([^/]+)\/pdf/i);
  if (match && match[1]) return match[1];
  return undefined;
};

const extractFilingIdFromMetricsValues = (
  values?: Record<string, FiscalStandardizedMetricValue>
): string | undefined => {
  if (!values) return undefined;
  for (const metric of Object.values(values)) {
    if (!metric || typeof metric !== 'object') continue;
    const asReported = (metric as { asReportedValues?: Array<{ sources?: Array<{ document?: string; originalSourceUrl?: string }> }> })
      .asReportedValues;
    if (!Array.isArray(asReported)) continue;
    for (const reported of asReported) {
      const sources = reported?.sources;
      if (!Array.isArray(sources)) continue;
      for (const source of sources) {
        const fromDocument = parseFilingIdFromUrl(source?.document);
        if (fromDocument) return fromDocument;
        const fromOriginal = parseFilingIdFromUrl(source?.originalSourceUrl);
        if (fromOriginal) return fromOriginal;
      }
    }
  }
  return undefined;
};

const extractFilingIdFromAsReportedMetricsValues = (
  values?: Record<string, FiscalAsReportedMetricValue>
): string | undefined => {
  if (!values) return undefined;
  for (const metric of Object.values(values)) {
    const sources = metric?.sources;
    if (!Array.isArray(sources)) continue;
    for (const source of sources) {
      const fromDocument = parseFilingIdFromUrl(source?.document);
      if (fromDocument) return fromDocument;
      const fromOriginal = parseFilingIdFromUrl(source?.originalSourceUrl);
      if (fromOriginal) return fromOriginal;
    }
  }
  return undefined;
};

const normalizeAsReportedDate = (row: FiscalAsReportedRow): string => {
  const direct = String(row.date ?? '').trim();
  if (direct) return direct;
  const periodEnd = resolveFiscalPeriodEndDate(row as Partial<FiscalStandardizedDataRow> & Record<string, unknown>);
  if (periodEnd) return periodEnd;
  return resolveFiscalReportDate(row as Partial<FiscalStandardizedDataRow> & Record<string, unknown>);
};

export type FiscalAsReportedFilingRow = {
  date: string;
  filingId?: string;
  periodType?: string;
};

export async function fetchFiscalIncomeStatementAsReportedFilingIds(
  symbol: string
): Promise<FiscalStatementResult<FiscalAsReportedFilingRow>> {
  return fetchFiscalAsReportedFilingIds(
    symbol,
    '/v1/company/financials/income-statement/as-reported'
  );
}

export async function fetchFiscalBalanceSheetAsReportedFilingIds(
  symbol: string
): Promise<FiscalStatementResult<FiscalAsReportedFilingRow>> {
  return fetchFiscalAsReportedFilingIds(
    symbol,
    '/v1/company/financials/balance-sheet/as-reported'
  );
}

export async function fetchFiscalCashFlowAsReportedFilingIds(
  symbol: string
): Promise<FiscalStatementResult<FiscalAsReportedFilingRow>> {
  return fetchFiscalAsReportedFilingIds(
    symbol,
    '/v1/company/financials/cash-flow-statement/as-reported'
  );
}

async function fetchFiscalAsReportedFilingIds(
  symbol: string,
  path: string
): Promise<FiscalStatementResult<FiscalAsReportedFilingRow>> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return { annual: [], quarter: [], error: 'Missing company key.' };

  const response = await fiscalGetWithError<FiscalAsReportedResponse | FiscalAsReportedRow[]>(
    path,
    { companyKey, periodType: 'annual,quarterly' }
  );

  const raw: FiscalAsReportedRow[] = Array.isArray(response.data)
    ? response.data
    : Array.isArray((response.data as FiscalAsReportedResponse | null)?.data)
      ? (response.data as FiscalAsReportedResponse).data ?? []
      : [];

  if (!raw.length) return { annual: [], quarter: [], error: response.error };

  const rows: FiscalAsReportedFilingRow[] = raw
    .map((row) => {
      const date = normalizeAsReportedDate(row);
      const filingId = String(
        row.filingId ??
          row.filing_id ??
          extractFilingIdFromAsReportedMetricsValues(row.metricsValues)
      ).trim();
      const periodType = String(row.periodType ?? '').trim().toLowerCase();
      return {
        date,
        filingId: filingId || undefined,
        periodType: periodType || undefined,
      };
    })
    .filter((row) => row.date);

  const annual = rows.filter((row) => String(row.periodType ?? '').includes('annual'));
  const quarter = rows.filter((row) => String(row.periodType ?? '').includes('quarter'));

  return { annual, quarter, error: response.error };
}

function parseAsReportedMetricNumber(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') return parseMetricNumber(value);
  const reported = (value as { asReportedValues?: Array<{ value?: unknown }> }).asReportedValues;
  const first = Array.isArray(reported) ? reported[0] : undefined;
  const reportedValue = first?.value;
  if (typeof reportedValue === 'number' && Number.isFinite(reportedValue)) return reportedValue;
  return parseMetricNumber(value);
}

function buildMetricIndex(metrics: FiscalStandardizedMetric[]) {
  const byName = new Map<string, FiscalStandardizedMetric>();
  const byId = new Map<string, FiscalStandardizedMetric>();

  metrics.forEach((metric) => {
    const name = String(metric.metricName || '').trim().toLowerCase();
    if (name) byName.set(name, metric);
    const id = String(metric.standardizedMetricId || '').trim().toLowerCase();
    if (id) byId.set(id, metric);
  });

  return { byName, byId };
}

function resolveMetricId(
  metrics: FiscalStandardizedMetric[],
  nameCandidates: string[],
  idCandidates: string[]
): string | null {
  if (!metrics.length) {
    const fallback = idCandidates.find((id) => String(id).trim().length > 0);
    return fallback ?? null;
  }
  const { byName, byId } = buildMetricIndex(metrics);

  for (const name of nameCandidates) {
    const key = String(name).trim().toLowerCase();
    if (byName.has(key)) return byName.get(key)!.standardizedMetricId;
  }

  for (const name of nameCandidates) {
    const key = String(name).trim().toLowerCase();
    const match = Array.from(byName.entries()).find(([candidate]) => candidate.includes(key));
    if (match) return match[1].standardizedMetricId;
  }

  for (const id of idCandidates) {
    const key = String(id).trim().toLowerCase();
    if (byId.has(key)) return byId.get(key)!.standardizedMetricId;
  }

  for (const id of idCandidates) {
    const key = String(id).trim().toLowerCase();
    const match = Array.from(byId.entries()).find(([candidate]) => candidate.includes(key));
    if (match) return match[1].standardizedMetricId;
  }

  return null;
}

function normalizeStandardizedPayload(
  payload: FiscalStandardizedResponse | null
): { metrics: FiscalStandardizedMetric[]; data: FiscalStandardizedDataRow[] } {
  if (!payload) return { metrics: [], data: [] };
  const data = Array.isArray(payload.data) ? payload.data : [];
  const metrics = Array.isArray(payload.metrics) ? payload.metrics : [];
  return { data, metrics };
}

function splitAnnualQuarter(rows: FiscalIncomeRow[] | FiscalCashRow[], source: FiscalStandardizedDataRow[]) {
  const annual: typeof rows = [] as typeof rows;
  const quarter: typeof rows = [] as typeof rows;

  source.forEach((row, idx) => {
    const periodType = String(row.periodType || '').toLowerCase();
    const target = periodType.includes('annual') ? annual : periodType.includes('quarter') ? quarter : null;
    if (!target) return;
    target.push(rows[idx]);
  });

  return { annual, quarter };
}

function splitAnnualQuarterBalance(rows: BalanceSheet[], source: FiscalStandardizedDataRow[]) {
  const annual: BalanceSheet[] = [];
  const quarter: BalanceSheet[] = [];

  source.forEach((row, idx) => {
    const periodType = String(row.periodType || '').toLowerCase();
    const target = periodType.includes('annual') ? annual : periodType.includes('quarter') ? quarter : null;
    if (!target) return;
    if (rows[idx]) target.push(rows[idx]);
  });

  return { annual, quarter };
}

async function fetchFiscalRatiosList(): Promise<FiscalRatioListItem[]> {
  const response = await fiscalGet<FiscalRatioListItem[]>('/v1/ratios-list', {});
  if (Array.isArray(response)) return response;
  return [];
}

async function resolveRoicRatioId(): Promise<string | null> {
  const list = await fetchFiscalRatiosList();
  if (!list.length) return null;

  const direct = list.find((item) =>
    String(item.metricName || '').toLowerCase().includes('return on invested capital')
  );
  if (direct?.ratioId) return direct.ratioId;

  const roicMatch = list.find((item) => String(item.metricName || '').toLowerCase().includes('roic'));
  return roicMatch?.ratioId ?? null;
}

async function resolveEffectiveTaxRateRatioId(): Promise<string | null> {
  const list = await fetchFiscalRatiosList();
  if (!list.length) return null;

  const direct = list.find((item) =>
    String(item.ratioId || '').toLowerCase() === 'ratio_effective_tax_rate'
  );
  if (direct?.ratioId) return direct.ratioId;

  const match = list.find((item) =>
    String(item.metricName || '').toLowerCase().includes('effective tax rate')
  );
  return match?.ratioId ?? null;
}

async function resolveMarketCapRatioId(): Promise<string | null> {
  const list = await fetchFiscalRatiosList();
  if (!list.length) return null;

  const direct = list.find((item) => {
    const id = String(item.ratioId || '').toLowerCase();
    return id === 'ratio_market_cap' || id === 'market_cap' || id.includes('market_cap');
  });
  if (direct?.ratioId) return direct.ratioId;

  const nameMatch = list.find((item) => {
    const name = String(item.metricName || '').toLowerCase();
    return name.includes('market cap') || name.includes('market capitalization');
  });
  return nameMatch?.ratioId ?? null;
}

export async function fetchFiscalRoicRatios(symbol: string, currency?: string): Promise<FiscalRatioRow[]> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return [];

  const ratioId = await resolveRoicRatioId();
  if (!ratioId) return [];

  const response = await fiscalGet<FiscalCompanyRatiosResponse>('/v1/company/ratios', {
    companyKey,
    periodType: 'annual,quarterly,ltm',
    ratioId,
    currency,
  });

  const rows = response?.data ?? [];
  return rows.map((row) => {
    const date = String(row.reportDate || '').trim();
    const periodRaw = String(row.periodType || '').toLowerCase();
    const periodType = periodRaw.includes('ltm')
      ? 'ltm'
      : periodRaw.includes('annual')
        ? 'annual'
        : periodRaw.includes('quarter')
          ? 'quarter'
          : 'other';
    const value = row.metricValues ? row.metricValues[ratioId] : undefined;
    return { date, periodType, value };
  });
}

const extractFiscalDescription = (payload: FiscalCompanyProfileResponse | null): string | null => {
  if (!payload) return null;
  const fromRoot = payload.description || payload.companyDescription || payload.businessDescription || payload.longDescription || payload.shortDescription || payload.overview;
  if (fromRoot && String(fromRoot).trim()) return String(fromRoot).trim();
  const fromProfile = payload.profile?.description || payload.profile?.longDescription || payload.profile?.overview;
  if (fromProfile && String(fromProfile).trim()) return String(fromProfile).trim();
  const fromData = payload.data?.description || payload.data?.companyDescription || payload.data?.businessDescription || payload.data?.longDescription || payload.data?.shortDescription || payload.data?.overview;
  if (fromData && String(fromData).trim()) return String(fromData).trim();
  return null;
};

export async function fetchFiscalCompanyDescription(symbol: string): Promise<string | null> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return null;

  const endpoints = ['/v1/company', '/v1/company/profile', '/v1/company/summary'];
  for (const path of endpoints) {
    const response = await fiscalGetWithError<FiscalCompanyProfileResponse>(path, { companyKey });
    const description = extractFiscalDescription(response.data ?? null);
    if (description) return description;
  }

  return null;
}

export async function fetchFiscalEffectiveTaxRateRatios(symbol: string): Promise<FiscalRatioRow[]> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return [];

  const ratioId = await resolveEffectiveTaxRateRatioId();
  if (!ratioId) return [];

  const response = await fiscalGet<FiscalCompanyRatiosResponse>('/v1/company/ratios', {
    companyKey,
    periodType: 'annual,quarterly,ltm',
    ratioId,
  });

  const rows = response?.data ?? [];
  return rows.map((row) => {
    const date = String(row.reportDate || '').trim();
    const periodRaw = String(row.periodType || '').toLowerCase();
    const periodType = periodRaw.includes('ltm')
      ? 'ltm'
      : periodRaw.includes('annual')
        ? 'annual'
        : periodRaw.includes('quarter')
          ? 'quarter'
          : 'other';
    const value = row.metricValues ? row.metricValues[ratioId] : undefined;
    return { date, periodType, value };
  });
}

export async function fetchFiscalMarketCapRatios(symbol: string): Promise<FiscalRatioRow[]> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return [];

  const ratioId = await resolveMarketCapRatioId();
  if (!ratioId) return [];

  const response = await fiscalGet<FiscalCompanyRatiosResponse>('/v1/company/ratios', {
    companyKey,
    periodType: 'annual,quarterly,ltm',
    ratioId,
  });

  const rows = response?.data ?? [];
  return rows.map((row) => {
    const date = String(row.reportDate || '').trim();
    const periodRaw = String(row.periodType || '').toLowerCase();
    const periodType = periodRaw.includes('ltm')
      ? 'ltm'
      : periodRaw.includes('annual')
        ? 'annual'
        : periodRaw.includes('quarter')
          ? 'quarter'
          : 'other';
    const value = row.metricValues ? row.metricValues[ratioId] : undefined;
    return { date, periodType, value };
  });
}

export async function fetchFiscalStandardizedMetricsList(): Promise<FiscalStandardizedMetric[]> {
  if (cachedStandardizedMetrics) return cachedStandardizedMetrics;
  if (standardizedMetricsPromise) return standardizedMetricsPromise;

  standardizedMetricsPromise = (async () => {
    const response = await fiscalGet<{ metrics?: FiscalStandardizedMetric[] }>(
      '/v1/standardized-metrics-list',
      {}
    );
    const metrics = response?.metrics ?? [];
    cachedStandardizedMetrics = metrics;
    return metrics;
  })();

  return standardizedMetricsPromise;
}

export async function fetchFiscalStandardizedIncomeStatement(
  symbol: string,
  metricCatalog?: FiscalStandardizedMetric[],
  currency?: string
): Promise<FiscalStatementResult<FiscalIncomeRow>> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return { annual: [], quarter: [], error: 'Missing company key.' };

  const response = await fiscalGetWithError<FiscalStandardizedResponse>(
    '/v1/company/financials/income-statement/standardized',
    { companyKey, periodType: 'annual,quarterly', currency }
  );

  const payload = normalizeStandardizedPayload(response.data);
  if (!payload.data.length) return { annual: [], quarter: [], error: response.error };
  const metrics = payload.metrics ?? metricCatalog ?? [];

  const revenueId = resolveMetricId(metrics, ['Total Revenues', 'Total Revenue', 'Revenues', 'Revenue'], [
    'income_statement_total_revenues',
    'income_statement_total_revenue',
    'income_statement_revenue',
  ]);
  const costOfRevenueId = resolveMetricId(metrics, ['Cost Of Sales', 'Cost of Revenue'], [
    'income_statement_cost_of_sales',
  ]);
  const grossProfitId = resolveMetricId(metrics, ['Gross Profit', 'Gross Profit (Loss)'], [
    'income_statement_gross_profit',
  ]);
  const rdId = resolveMetricId(metrics, ['Research And Development Expenses', 'Research & Development', 'R&D Expenses'], [
    'income_statement_research_and_development_expenses',
  ]);
  const sgaId = resolveMetricId(
    metrics,
    ['Selling General And Administrative Expenses', 'Selling General and Administrative Expenses', 'SG&A'],
    ['income_statement_selling_general_and_administrative_expenses']
  );
  const interestIncomeId = resolveMetricId(metrics, ['Interest Income'], [
    'income_statement_interest_income',
  ]);
  const interestExpenseId = resolveMetricId(metrics, ['Interest Expense'], [
    'income_statement_interest_expense',
  ]);
  const incomeTaxExpenseId = resolveMetricId(metrics, ['Provision For Income Taxes', 'Income Tax Provision'], [
    'income_statement_provision_for_income_taxes',
  ]);
  const incomeBeforeTaxId = resolveMetricId(metrics, ['Income Before Provision For Income Taxes', 'Income Before Income Taxes'], [
    'income_statement_income_before_provision_for_income_taxes',
  ]);
  const nonOperatingIncomeId = resolveMetricId(
    metrics,
    ['Other Income (Expense)', 'Non Operating Income', 'Non-operating Income'],
    ['income_statement_non_operating_income']
  );
  const dilutedSharesId = resolveMetricId(metrics, ['Diluted Weighted Average Shares Outstanding'], [
    'income_statement_diluted_weighted_average_shares_outstanding',
  ]);
  const operatingIncomeId = resolveMetricId(metrics, ['Operating Income', 'Operating Income (Loss)', 'Operating Profit'], [
    'income_statement_operating_income',
  ]);
  const netIncomeId = resolveMetricId(
    metrics,
    ['Net Income Attributable to Common Shareholders'],
    ['income_statement_net_income_attributable_to_common_shareholders']
  );
  const epsDilutedId = resolveMetricId(metrics, ['EPS Diluted', 'Diluted EPS', 'Earnings Per Share Diluted'], [
    'income_statement_diluted_eps',
    'income_statement_eps_diluted',
  ]);

  const rows: FiscalIncomeRow[] = payload.data.map((row) => {
    const periodEndDate = resolveFiscalPeriodEndDate(row);
    const filingDate = resolveFiscalFilingDate(row);
    const earningDate = resolveFiscalEarningsDate(row);
    const date = periodEndDate || filingDate;
    const values = row.metricsValues ?? {};
    const filingId = row.filingId ?? row.filing_id ?? extractFilingIdFromMetricsValues(values);

    const get = (id: string | null) => (id ? parseMetricNumber(values[id]) : undefined);

    const revenue = get(revenueId);
    const costOfRevenue = get(costOfRevenueId);
    const grossProfit = get(grossProfitId);
    const researchAndDevelopmentExpenses = get(rdId);
    const sellingGeneralAndAdministrativeExpenses = get(sgaId);
    const interestIncome = get(interestIncomeId);
    const netInterestIncome = get(interestIncomeId);
    const interestExpense = get(interestExpenseId);
    const incomeTaxExpense = get(incomeTaxExpenseId);
    const incomeBeforeTax = get(incomeBeforeTaxId);
    const nonOperatingIncome = get(nonOperatingIncomeId);
    const operatingExpenses = get(sgaId);
    const dilutedWeightedAverageSharesOutstanding = get(dilutedSharesId);
    const operatingIncome = get(operatingIncomeId);
    const netIncome = netIncomeId ? parseMetricNumber(values[netIncomeId]) : undefined;
    const epsDiluted = epsDilutedId ? parseMetricNumber(values[epsDilutedId]) : undefined;

    return {
      date,
      periodEndDate: periodEndDate || undefined,
      filingDate: filingDate || undefined,
      filingId: filingId || undefined,
      earningDate: earningDate || undefined,
      calendarYear: row.calendarYear,
      calendarQuarter: row.calendarQuarter,
      fiscalYear: row.fiscalYear,
      fiscalQuarter: row.fiscalQuarter,
      revenue,
      costOfRevenue,
      grossProfit,
      researchAndDevelopmentExpenses,
      sellingGeneralAndAdministrativeExpenses,
      interestIncome,
      netInterestIncome,
      interestExpense,
      operatingIncome,
      incomeBeforeTax,
      incomeTaxExpense,
      nonOperatingIncome,
      operatingExpenses,
      netIncome,
      epsDiluted,
      eps: epsDiluted,
      dilutedWeightedAverageSharesOutstanding,
    };
  });

  return { ...splitAnnualQuarter(rows, payload.data), error: response.error };
}

export async function fetchFiscalStandardizedIncomeStatementTtm(
  symbol: string,
  metricCatalog?: FiscalStandardizedMetric[],
  currency?: string
): Promise<FiscalTtmResult<FiscalIncomeRow>> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return { row: null, error: 'Missing company key.' };

  const response = await fiscalGetWithError<FiscalStandardizedResponse>(
    '/v1/company/financials/income-statement/standardized',
    { companyKey, periodType: 'ltm', currency }
  );

  const payload = normalizeStandardizedPayload(response.data);
  if (!payload.data.length) return { row: null, error: response.error };
  const metrics = payload.metrics ?? metricCatalog ?? [];

  const revenueId = resolveMetricId(metrics, ['Total Revenues', 'Total Revenue', 'Revenues', 'Revenue'], [
    'income_statement_total_revenues',
    'income_statement_total_revenue',
    'income_statement_revenue',
  ]);
  const costOfRevenueId = resolveMetricId(metrics, ['Cost Of Sales', 'Cost of Revenue'], [
    'income_statement_cost_of_sales',
  ]);
  const grossProfitId = resolveMetricId(metrics, ['Gross Profit', 'Gross Profit (Loss)'], [
    'income_statement_gross_profit',
  ]);
  const rdId = resolveMetricId(metrics, ['Research And Development Expenses', 'Research & Development', 'R&D Expenses'], [
    'income_statement_research_and_development_expenses',
  ]);
  const sgaId = resolveMetricId(
    metrics,
    ['Selling General And Administrative Expenses', 'Selling General and Administrative Expenses', 'SG&A'],
    ['income_statement_selling_general_and_administrative_expenses']
  );
  const interestIncomeId = resolveMetricId(metrics, ['Interest Income'], [
    'income_statement_interest_income',
  ]);
  const interestExpenseId = resolveMetricId(metrics, ['Interest Expense'], [
    'income_statement_interest_expense',
  ]);
  const incomeTaxExpenseId = resolveMetricId(metrics, ['Provision For Income Taxes', 'Income Tax Provision'], [
    'income_statement_provision_for_income_taxes',
  ]);
  const incomeBeforeTaxId = resolveMetricId(metrics, ['Income Before Provision For Income Taxes', 'Income Before Income Taxes'], [
    'income_statement_income_before_provision_for_income_taxes',
  ]);
  const nonOperatingIncomeId = resolveMetricId(
    metrics,
    ['Other Income (Expense)', 'Non Operating Income', 'Non-operating Income'],
    ['income_statement_non_operating_income']
  );
  const dilutedSharesId = resolveMetricId(metrics, ['Diluted Weighted Average Shares Outstanding'], [
    'income_statement_diluted_weighted_average_shares_outstanding',
  ]);
  const operatingIncomeId = resolveMetricId(metrics, ['Operating Income', 'Operating Income (Loss)', 'Operating Profit'], [
    'income_statement_operating_income',
  ]);
  const netIncomeId = resolveMetricId(
    metrics,
    ['Net Income Attributable to Common Shareholders'],
    ['income_statement_net_income_attributable_to_common_shareholders']
  );
  const epsDilutedId = resolveMetricId(metrics, ['EPS Diluted', 'Diluted EPS', 'Earnings Per Share Diluted'], [
    'income_statement_diluted_eps',
    'income_statement_eps_diluted',
  ]);

  const first = payload.data[0];
  const values = first.metricsValues ?? {};
  const get = (id: string | null) => (id ? parseMetricNumber(values[id]) : undefined);

  const periodEndDate = resolveFiscalPeriodEndDate(first);
  const filingDate = resolveFiscalFilingDate(first);
  const earningDate = resolveFiscalEarningsDate(first);

  const row: FiscalIncomeRow = {
    date: periodEndDate || filingDate,
    periodEndDate: periodEndDate || undefined,
    filingDate: filingDate || undefined,
    filingId: first.filingId ?? first.filing_id,
    earningDate: earningDate || undefined,
    calendarYear: first.calendarYear,
    calendarQuarter: first.calendarQuarter,
    fiscalYear: first.fiscalYear,
    fiscalQuarter: first.fiscalQuarter,
    revenue: get(revenueId),
    costOfRevenue: get(costOfRevenueId),
    grossProfit: get(grossProfitId),
    researchAndDevelopmentExpenses: get(rdId),
    sellingGeneralAndAdministrativeExpenses: get(sgaId),
    interestIncome: get(interestIncomeId),
    netInterestIncome: get(interestIncomeId),
    interestExpense: get(interestExpenseId),
    incomeTaxExpense: get(incomeTaxExpenseId),
    incomeBeforeTax: get(incomeBeforeTaxId),
    nonOperatingIncome: get(nonOperatingIncomeId),
    operatingExpenses: get(sgaId),
    dilutedWeightedAverageSharesOutstanding: get(dilutedSharesId),
    operatingIncome: get(operatingIncomeId),
    netIncome: netIncomeId ? parseMetricNumber(values[netIncomeId]) : undefined,
    epsDiluted: epsDilutedId ? parseMetricNumber(values[epsDilutedId]) : undefined,
    eps: epsDilutedId ? parseMetricNumber(values[epsDilutedId]) : undefined,
  };

  return { row, date: row.date, error: response.error };
}

export async function fetchFiscalStandardizedCashFlowStatement(
  symbol: string,
  metricCatalog?: FiscalStandardizedMetric[],
  currency?: string
): Promise<FiscalStatementResult<FiscalCashRow>> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return { annual: [], quarter: [], error: 'Missing company key.' };

  const response = await fiscalGetWithError<FiscalStandardizedResponse>(
    '/v1/company/financials/cash-flow-statement/standardized',
    { companyKey, periodType: 'annual,quarterly', currency }
  );

  const payload = normalizeStandardizedPayload(response.data);
  if (!payload.data.length) return { annual: [], quarter: [], error: response.error };
  const metrics = payload.metrics ?? metricCatalog ?? [];

  const operatingCashFlowId = resolveMetricId(
    metrics,
    ['Net Cash Provided By Operating Activities', 'Operating Cash Flow'],
    [
      'cash_flow_net_cash_provided_by_operating_activities',
      'cash_flow_operating_cash_flow',
      'cash_flow_statement_cash_from_operating_activities',
    ]
  );
  const netIncomeId = resolveMetricId(metrics, ['Net Income'], ['cash_flow_statement_net_income']);
  const depreciationId = resolveMetricId(metrics, ['Depreciation And Amortization', 'Depreciation & Amortization'], [
    'cash_flow_statement_depreciation_and_amortization',
  ]);
  const stockCompId = resolveMetricId(metrics, ['Share Based Compensation Expense', 'Stock Based Compensation'], [
    'cash_flow_statement_share_based_compensation_expense',
  ]);
  const otherAdjustmentsId = resolveMetricId(metrics, ['Other Adjustments'], [
    'cash_flow_statement_other_adjustments',
  ]);
  const receivablesId = resolveMetricId(metrics, ['Changes in Trade Receivables'], [
    'cash_flow_statement_changes_in_trade_receivables',
  ]);
  const inventoriesId = resolveMetricId(metrics, ['Changes in Inventories'], [
    'cash_flow_statement_changes_in_inventories',
  ]);
  const accountsPayableId = resolveMetricId(metrics, ['Changes in Accounts Payable'], [
    'cash_flow_statement_changes_in_accounts_payable',
  ]);
  const accruedExpensesId = resolveMetricId(metrics, ['Changes in Accrued Expenses'], [
    'cash_flow_statement_changes_in_accrued_expenses',
  ]);
  const incomeTaxesId = resolveMetricId(metrics, ['Changes in Income Taxes'], [
    'cash_flow_statement_changes_in_income_taxes_payable',
  ]);
  const deferredRevenueId = resolveMetricId(metrics, ['Changes in Deferred Revenue'], [
    'cash_flow_statement_changes_in_unearned_revenue',
  ]);
  const otherOperatingId = resolveMetricId(metrics, ['Changes in Other Operating Activities'], [
    'cash_flow_statement_changes_in_other_operating_activities',
  ]);
  const cashFromOperatingId = resolveMetricId(metrics, ['Cash From Operating Activities'], [
    'cash_flow_statement_cash_from_operating_activities',
  ]);
  const ppeId = resolveMetricId(metrics, ['Purchases of Property, Plant and Equipment'], [
    'cash_flow_statement_purchases_of_property_plant_and_equipment',
  ]);
  const purchasesInvestmentsId = resolveMetricId(metrics, ['Purchases of Investments'], [
    'cash_flow_statement_purchases_of_investments',
  ]);
  const proceedsInvestmentsId = resolveMetricId(metrics, ['Proceeds From Sale Of Investments'], [
    'cash_flow_statement_proceeds_from_sale_of_investments',
  ]);
  const acquisitionsId = resolveMetricId(metrics, ['Payments For Business Acquisitions'], [
    'cash_flow_statement_payments_for_business_acquisitions',
  ]);
  const otherInvestingId = resolveMetricId(metrics, ['Other Investing Activities'], [
    'cash_flow_statement_other_investing_activities',
  ]);
  const cashFromInvestingId = resolveMetricId(metrics, ['Cash From Investing Activities'], [
    'cash_flow_statement_cash_from_investing_activities',
  ]);
  const netDebtIssuanceId = resolveMetricId(metrics, ['Net Issuance Or Repayments Of Long Term Debt'], [
    'cash_flow_statement_net_issuance_or_repayments_of_long_term_debt',
  ]);
  const commonSharesIssuedId = resolveMetricId(metrics, ['Issuance Of Common Shares'], [
    'cash_flow_statement_issuance_of_common_shares',
  ]);
  const commonSharesRepurchasedId = resolveMetricId(metrics, ['Repurchases Of Common Shares'], [
    'cash_flow_statement_repurchases_of_common_shares',
  ]);
  const commonDividendsPaidId = resolveMetricId(metrics, ['Common Share Dividends Paid'], [
    'cash_flow_statement_common_share_dividends_paid',
  ]);
  const dividendsPaidId = resolveMetricId(
    metrics,
    ['Dividends Paid', 'Cash Dividends Paid', 'Payments Of Dividends'],
    ['cash_flow_statement_dividends_paid', 'cash_flow_dividends_paid']
  );
  const otherFinancingId = resolveMetricId(metrics, ['Other Financing Activities'], [
    'cash_flow_statement_other_financing_activities',
  ]);
  const cashFromFinancingId = resolveMetricId(metrics, ['Cash From Financing Activities'], [
    'cash_flow_statement_cash_from_financing_activities',
  ]);
  const forexEffectId = resolveMetricId(metrics, ['Effect Of Exchange Rate Changes On Cash And Cash Equivalents'], [
    'cash_flow_statement_effect_of_exchange_rate_changes_on_cash_and_cash_equivalents',
  ]);
  const netChangeCashId = resolveMetricId(metrics, ['Increase Or Decrease In Cash Cash Equivalents And Restricted Cash'], [
    'cash_flow_statement_increase_or_decrease_in_cash_cash_equivalents_and_restricted_cash',
  ]);
  const rows: FiscalCashRow[] = payload.data.map((row) => {
    const periodEndDate = resolveFiscalPeriodEndDate(row);
    const filingDate = resolveFiscalFilingDate(row);
    const earningDate = resolveFiscalEarningsDate(row);
    const date = periodEndDate || filingDate;
    const values = row.metricsValues ?? {};

    const operatingCashFlow = operatingCashFlowId ? parseMetricNumber(values[operatingCashFlowId]) : undefined;
    const netIncome = netIncomeId ? parseMetricNumber(values[netIncomeId]) : undefined;
    const depreciationAndAmortization = depreciationId ? parseMetricNumber(values[depreciationId]) : undefined;
    const stockBasedCompensation = stockCompId ? parseMetricNumber(values[stockCompId]) : undefined;
    const changeInWorkingCapital = otherAdjustmentsId ? parseMetricNumber(values[otherAdjustmentsId]) : undefined;
    const accountsReceivables = receivablesId ? parseMetricNumber(values[receivablesId]) : undefined;
    const inventory = inventoriesId ? parseMetricNumber(values[inventoriesId]) : undefined;
    const accountsPayables = accountsPayableId ? parseMetricNumber(values[accountsPayableId]) : undefined;
    const otherWorkingCapital = accruedExpensesId ? parseMetricNumber(values[accruedExpensesId]) : undefined;
    const changesInIncomeTaxes = incomeTaxesId ? parseMetricNumber(values[incomeTaxesId]) : undefined;
    const changesInDeferredRevenue = deferredRevenueId ? parseMetricNumber(values[deferredRevenueId]) : undefined;
    const otherNonCashItems = otherOperatingId ? parseMetricNumber(values[otherOperatingId]) : undefined;
    const cashFromOperating = cashFromOperatingId ? parseMetricNumber(values[cashFromOperatingId]) : undefined;
    const investmentsInPropertyPlantAndEquipment = ppeId ? parseMetricNumber(values[ppeId]) : undefined;
    const purchasesOfInvestments = purchasesInvestmentsId ? parseMetricNumber(values[purchasesInvestmentsId]) : undefined;
    const salesMaturitiesOfInvestments = proceedsInvestmentsId ? parseMetricNumber(values[proceedsInvestmentsId]) : undefined;
    const acquisitionsNet = acquisitionsId ? parseMetricNumber(values[acquisitionsId]) : undefined;
    const otherInvestingActivities = otherInvestingId ? parseMetricNumber(values[otherInvestingId]) : undefined;
    const netCashUsedForInvestingActivities = cashFromInvestingId ? parseMetricNumber(values[cashFromInvestingId]) : undefined;
    const debtRepayment = netDebtIssuanceId ? parseMetricNumber(values[netDebtIssuanceId]) : undefined;
    const commonStockIssued = commonSharesIssuedId ? parseMetricNumber(values[commonSharesIssuedId]) : undefined;
    const commonStockRepurchased = commonSharesRepurchasedId ? parseMetricNumber(values[commonSharesRepurchasedId]) : undefined;
    const commonDividendsPaid = commonDividendsPaidId ? parseMetricNumber(values[commonDividendsPaidId]) : undefined;
    const dividendsPaid = dividendsPaidId ? parseMetricNumber(values[dividendsPaidId]) : undefined;
    const otherFinancingActivities = otherFinancingId ? parseMetricNumber(values[otherFinancingId]) : undefined;
    const netCashUsedProvidedByFinancingActivities = cashFromFinancingId ? parseMetricNumber(values[cashFromFinancingId]) : undefined;
    const effectOfForexChangesOnCash = forexEffectId ? parseMetricNumber(values[forexEffectId]) : undefined;
    const netChangeInCash = netChangeCashId ? parseMetricNumber(values[netChangeCashId]) : undefined;

    return {
      date,
      periodEndDate: periodEndDate || undefined,
      filingDate: filingDate || undefined,
      earningDate: earningDate || undefined,
      calendarYear: row.calendarYear,
      calendarQuarter: row.calendarQuarter,
      fiscalYear: row.fiscalYear,
      fiscalQuarter: row.fiscalQuarter,
      netIncome,
      depreciationAndAmortization,
      stockBasedCompensation,
      changeInWorkingCapital,
      accountsReceivables,
      inventory,
      accountsPayables,
      otherWorkingCapital,
      changesInIncomeTaxes,
      changesInDeferredRevenue,
      otherNonCashItems,
      operatingCashFlow,
      netCashProvidedByOperatingActivities: cashFromOperating ?? operatingCashFlow,
      investmentsInPropertyPlantAndEquipment,
      purchasesOfInvestments,
      salesMaturitiesOfInvestments,
      acquisitionsNet,
      otherInvestingActivities,
      netCashUsedForInvestingActivities,
      debtRepayment,
      commonStockIssued,
      commonStockRepurchased,
      commonDividendsPaid,
      dividendsPaid: dividendsPaid ?? commonDividendsPaid,
      otherFinancingActivities,
      netCashUsedProvidedByFinancingActivities,
      effectOfForexChangesOnCash,
      netChangeInCash,
      capitalExpenditure: investmentsInPropertyPlantAndEquipment,
    };
  });

  return { ...splitAnnualQuarter(rows, payload.data), error: response.error };
}

export async function fetchFiscalStandardizedCashFlowStatementTtm(
  symbol: string,
  metricCatalog?: FiscalStandardizedMetric[],
  currency?: string
): Promise<FiscalTtmResult<FiscalCashRow>> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return { row: null, error: 'Missing company key.' };

  const response = await fiscalGetWithError<FiscalStandardizedResponse>(
    '/v1/company/financials/cash-flow-statement/standardized',
    { companyKey, periodType: 'ltm', currency }
  );

  const payload = normalizeStandardizedPayload(response.data);
  if (!payload.data.length) return { row: null, error: response.error };
  const metrics = payload.metrics ?? metricCatalog ?? [];

  const operatingCashFlowId = resolveMetricId(
    metrics,
    ['Net Cash Provided By Operating Activities', 'Operating Cash Flow'],
    [
      'cash_flow_net_cash_provided_by_operating_activities',
      'cash_flow_operating_cash_flow',
      'cash_flow_statement_cash_from_operating_activities',
    ]
  );
  const netIncomeId = resolveMetricId(metrics, ['Net Income'], ['cash_flow_statement_net_income']);
  const depreciationId = resolveMetricId(metrics, ['Depreciation And Amortization', 'Depreciation & Amortization'], [
    'cash_flow_statement_depreciation_and_amortization',
  ]);
  const stockCompId = resolveMetricId(metrics, ['Share Based Compensation Expense', 'Stock Based Compensation'], [
    'cash_flow_statement_share_based_compensation_expense',
  ]);
  const otherAdjustmentsId = resolveMetricId(metrics, ['Other Adjustments'], [
    'cash_flow_statement_other_adjustments',
  ]);
  const receivablesId = resolveMetricId(metrics, ['Changes in Trade Receivables'], [
    'cash_flow_statement_changes_in_trade_receivables',
  ]);
  const inventoriesId = resolveMetricId(metrics, ['Changes in Inventories'], [
    'cash_flow_statement_changes_in_inventories',
  ]);
  const accountsPayableId = resolveMetricId(metrics, ['Changes in Accounts Payable'], [
    'cash_flow_statement_changes_in_accounts_payable',
  ]);
  const accruedExpensesId = resolveMetricId(metrics, ['Changes in Accrued Expenses'], [
    'cash_flow_statement_changes_in_accrued_expenses',
  ]);
  const incomeTaxesId = resolveMetricId(metrics, ['Changes in Income Taxes'], [
    'cash_flow_statement_changes_in_income_taxes_payable',
  ]);
  const deferredRevenueId = resolveMetricId(metrics, ['Changes in Deferred Revenue'], [
    'cash_flow_statement_changes_in_unearned_revenue',
  ]);
  const otherOperatingId = resolveMetricId(metrics, ['Changes in Other Operating Activities'], [
    'cash_flow_statement_changes_in_other_operating_activities',
  ]);
  const cashFromOperatingId = resolveMetricId(metrics, ['Cash From Operating Activities'], [
    'cash_flow_statement_cash_from_operating_activities',
  ]);
  const ppeId = resolveMetricId(metrics, ['Purchases of Property, Plant and Equipment'], [
    'cash_flow_statement_purchases_of_property_plant_and_equipment',
  ]);
  const purchasesInvestmentsId = resolveMetricId(metrics, ['Purchases of Investments'], [
    'cash_flow_statement_purchases_of_investments',
  ]);
  const proceedsInvestmentsId = resolveMetricId(metrics, ['Proceeds From Sale Of Investments'], [
    'cash_flow_statement_proceeds_from_sale_of_investments',
  ]);
  const acquisitionsId = resolveMetricId(metrics, ['Payments For Business Acquisitions'], [
    'cash_flow_statement_payments_for_business_acquisitions',
  ]);
  const otherInvestingId = resolveMetricId(metrics, ['Other Investing Activities'], [
    'cash_flow_statement_other_investing_activities',
  ]);
  const cashFromInvestingId = resolveMetricId(metrics, ['Cash From Investing Activities'], [
    'cash_flow_statement_cash_from_investing_activities',
  ]);
  const netDebtIssuanceId = resolveMetricId(metrics, ['Net Issuance Or Repayments Of Long Term Debt'], [
    'cash_flow_statement_net_issuance_or_repayments_of_long_term_debt',
  ]);
  const commonSharesIssuedId = resolveMetricId(metrics, ['Issuance Of Common Shares'], [
    'cash_flow_statement_issuance_of_common_shares',
  ]);
  const commonSharesRepurchasedId = resolveMetricId(metrics, ['Repurchases Of Common Shares'], [
    'cash_flow_statement_repurchases_of_common_shares',
  ]);
  const commonDividendsPaidId = resolveMetricId(metrics, ['Common Share Dividends Paid'], [
    'cash_flow_statement_common_share_dividends_paid',
  ]);
  const dividendsPaidId = resolveMetricId(
    metrics,
    ['Dividends Paid', 'Cash Dividends Paid', 'Payments Of Dividends'],
    ['cash_flow_statement_dividends_paid', 'cash_flow_dividends_paid']
  );
  const otherFinancingId = resolveMetricId(metrics, ['Other Financing Activities'], [
    'cash_flow_statement_other_financing_activities',
  ]);
  const cashFromFinancingId = resolveMetricId(metrics, ['Cash From Financing Activities'], [
    'cash_flow_statement_cash_from_financing_activities',
  ]);
  const forexEffectId = resolveMetricId(metrics, ['Effect Of Exchange Rate Changes On Cash And Cash Equivalents'], [
    'cash_flow_statement_effect_of_exchange_rate_changes_on_cash_and_cash_equivalents',
  ]);
  const netChangeCashId = resolveMetricId(metrics, ['Increase Or Decrease In Cash Cash Equivalents And Restricted Cash'], [
    'cash_flow_statement_increase_or_decrease_in_cash_cash_equivalents_and_restricted_cash',
  ]);

  const first = payload.data[0];
  const values = first.metricsValues ?? {};
  const operatingCashFlow = operatingCashFlowId ? parseMetricNumber(values[operatingCashFlowId]) : undefined;
  const netIncome = netIncomeId ? parseMetricNumber(values[netIncomeId]) : undefined;
  const depreciationAndAmortization = depreciationId ? parseMetricNumber(values[depreciationId]) : undefined;
  const stockBasedCompensation = stockCompId ? parseMetricNumber(values[stockCompId]) : undefined;
  const changeInWorkingCapital = otherAdjustmentsId ? parseMetricNumber(values[otherAdjustmentsId]) : undefined;
  const accountsReceivables = receivablesId ? parseMetricNumber(values[receivablesId]) : undefined;
  const inventory = inventoriesId ? parseMetricNumber(values[inventoriesId]) : undefined;
  const accountsPayables = accountsPayableId ? parseMetricNumber(values[accountsPayableId]) : undefined;
  const otherWorkingCapital = accruedExpensesId ? parseMetricNumber(values[accruedExpensesId]) : undefined;
  const changesInIncomeTaxes = incomeTaxesId ? parseMetricNumber(values[incomeTaxesId]) : undefined;
  const changesInDeferredRevenue = deferredRevenueId ? parseMetricNumber(values[deferredRevenueId]) : undefined;
  const otherNonCashItems = otherOperatingId ? parseMetricNumber(values[otherOperatingId]) : undefined;
  const cashFromOperating = cashFromOperatingId ? parseMetricNumber(values[cashFromOperatingId]) : undefined;
  const investmentsInPropertyPlantAndEquipment = ppeId ? parseMetricNumber(values[ppeId]) : undefined;
  const purchasesOfInvestments = purchasesInvestmentsId ? parseMetricNumber(values[purchasesInvestmentsId]) : undefined;
  const salesMaturitiesOfInvestments = proceedsInvestmentsId ? parseMetricNumber(values[proceedsInvestmentsId]) : undefined;
  const acquisitionsNet = acquisitionsId ? parseMetricNumber(values[acquisitionsId]) : undefined;
  const otherInvestingActivities = otherInvestingId ? parseMetricNumber(values[otherInvestingId]) : undefined;
  const netCashUsedForInvestingActivities = cashFromInvestingId ? parseMetricNumber(values[cashFromInvestingId]) : undefined;
  const debtRepayment = netDebtIssuanceId ? parseMetricNumber(values[netDebtIssuanceId]) : undefined;
  const commonStockIssued = commonSharesIssuedId ? parseMetricNumber(values[commonSharesIssuedId]) : undefined;
  const commonStockRepurchased = commonSharesRepurchasedId ? parseMetricNumber(values[commonSharesRepurchasedId]) : undefined;
  const commonDividendsPaid = commonDividendsPaidId ? parseMetricNumber(values[commonDividendsPaidId]) : undefined;
  const dividendsPaid = dividendsPaidId ? parseMetricNumber(values[dividendsPaidId]) : undefined;
  const otherFinancingActivities = otherFinancingId ? parseMetricNumber(values[otherFinancingId]) : undefined;
  const netCashUsedProvidedByFinancingActivities = cashFromFinancingId ? parseMetricNumber(values[cashFromFinancingId]) : undefined;
  const effectOfForexChangesOnCash = forexEffectId ? parseMetricNumber(values[forexEffectId]) : undefined;
  const netChangeInCash = netChangeCashId ? parseMetricNumber(values[netChangeCashId]) : undefined;

  const periodEndDate = resolveFiscalPeriodEndDate(first);
  const filingDate = resolveFiscalFilingDate(first);
  const earningDate = resolveFiscalEarningsDate(first);
  const row: FiscalCashRow = {
    date: periodEndDate || filingDate,
    periodEndDate: periodEndDate || undefined,
    filingDate: filingDate || undefined,
    earningDate: earningDate || undefined,
    calendarYear: first.calendarYear,
    calendarQuarter: first.calendarQuarter,
    fiscalYear: first.fiscalYear,
    fiscalQuarter: first.fiscalQuarter,
    netIncome,
    depreciationAndAmortization,
    stockBasedCompensation,
    changeInWorkingCapital,
    accountsReceivables,
    inventory,
    accountsPayables,
    otherWorkingCapital,
    changesInIncomeTaxes,
    changesInDeferredRevenue,
    otherNonCashItems,
    operatingCashFlow,
    netCashProvidedByOperatingActivities: cashFromOperating ?? operatingCashFlow,
    investmentsInPropertyPlantAndEquipment,
    purchasesOfInvestments,
    salesMaturitiesOfInvestments,
    acquisitionsNet,
    otherInvestingActivities,
    netCashUsedForInvestingActivities,
    debtRepayment,
    commonStockIssued,
    commonStockRepurchased,
    commonDividendsPaid,
    dividendsPaid: dividendsPaid ?? commonDividendsPaid,
    otherFinancingActivities,
    netCashUsedProvidedByFinancingActivities,
    effectOfForexChangesOnCash,
    netChangeInCash,
    capitalExpenditure: investmentsInPropertyPlantAndEquipment,
  };

  return { row, date: row.date, error: response.error };
}

export async function fetchFiscalStandardizedBalanceSheet(
  symbol: string,
  metricCatalog?: FiscalStandardizedMetric[],
  currency?: string
): Promise<FiscalStatementResult<BalanceSheet>> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return { annual: [], quarter: [], error: 'Missing company key.' };

  const response = await fiscalGetWithError<FiscalStandardizedResponse>(
    '/v1/company/financials/balance-sheet/standardized',
    { companyKey, periodType: 'annual,quarterly', currency }
  );

  const payload = normalizeStandardizedPayload(response.data);
  if (!payload.data.length) return { annual: [], quarter: [], error: response.error };
  const metrics = payload.metrics ?? metricCatalog ?? [];

  const cashId = resolveMetricId(metrics, ['Cash And Cash Equivalents', 'Cash & Equivalents'], [
    'balance_sheet_cash_and_cash_equivalents',
  ]);
  const shortTermInvestmentsId = resolveMetricId(metrics, [], ['balance_sheet_short_term_investments']);
  const cashAndShortTermId = resolveMetricId(metrics, ['Cash and Short Term Investments'], [
    'balance_sheet_total_cash_and_cash_equivalents',
  ]);
  const receivablesId = resolveMetricId(metrics, ['Receivables', 'Accounts Receivable'], [
    'balance_sheet_accounts_receivable',
  ]);
  const inventoryId = resolveMetricId(metrics, [], ['balance_sheet_inventories']);
  const otherCurrentAssetsId = resolveMetricId(metrics, ['Other Current Assets'], [
    'balance_sheet_other_current_assets',
  ]);
  const totalCurrentAssetsId = resolveMetricId(metrics, ['Total Current Assets'], [
    'balance_sheet_total_current_assets',
  ]);
  const ppeId = resolveMetricId(metrics, ['Property, Plant and Equipment', 'Property, Plant & Equipment'], [
    'balance_sheet_net_property_plant_and_equipment',
  ]);
  const goodwillId = resolveMetricId(metrics, ['Goodwill'], ['balance_sheet_goodwill']);
  const intangiblesId = resolveMetricId(metrics, ['Intangible Assets'], ['balance_sheet_net_intangible_assets']);
  const longTermInvestmentsId = resolveMetricId(metrics, ['Long Term Investments'], [
    'balance_sheet_long_term_investments',
  ]);
  const otherLongTermAssetsId = resolveMetricId(metrics, ['Other Long Term Assets'], [
    'balance_sheet_other_long_term_assets',
  ]);
  const totalAssetsId = resolveMetricId(metrics, ['Total Assets'], ['balance_sheet_total_assets']);
  const accountsPayableId = resolveMetricId(metrics, ['Accounts Payable'], [
    'balance_sheet_accounts_payable',
  ]);
  const accruedExpensesId = resolveMetricId(metrics, ['Accrued Expenses'], [
    'balance_sheet_accrued_expenses',
  ]);
  const shortTermDebtId = resolveMetricId(metrics, [], ['balance_sheet_short_term_debt']);
  const taxPayablesId = resolveMetricId(metrics, ['Tax Payables', 'Income Taxes Payable'], [
    'balance_sheet_tax_payable',
  ]);
  const deferredRevenueId = resolveMetricId(metrics, ['Deferred Revenue', 'Unearned Revenue'], [
    'balance_sheet_unearned_revenue',
  ]);
  const otherCurrentLiabilitiesId = resolveMetricId(metrics, ['Other Current Liabilities'], [
    'balance_sheet_other_current_liabilities',
  ]);
  const totalCurrentLiabilitiesId = resolveMetricId(metrics, ['Total Current Liabilities'], [
    'balance_sheet_total_current_liabilities',
  ]);
  const longTermDebtId = resolveMetricId(metrics, ['Total Long Term Debt', 'Long Term Debt'], [
    'balance_sheet_long_term_debt',
  ]);
  const leasesId = resolveMetricId(metrics, ['Capital Lease Obligations', 'Leases'], [
    'balance_sheet_leases',
  ]);
  const deferredRevenueNonCurrentId = resolveMetricId(metrics, ['Deferred Revenue Non-Current'], [
    'balance_sheet_deferred_revenue_non_current',
  ]);
  const deferredTaxLiabilitiesNonCurrentId = resolveMetricId(metrics, ['Deferred Tax Liabilities Non-Current'], [
    'balance_sheet_deferred_tax_liabilities_non_current',
  ]);
  const otherLongTermLiabilitiesId = resolveMetricId(metrics, ['Other Long Term Liabilities'], [
    'balance_sheet_other_long_term_liabilities',
  ]);
  const totalLongTermLiabilitiesId = resolveMetricId(metrics, ['Total Long-Term Liabilities'], [
    'balance_sheet_total_long_term_liabilities',
  ]);
  const totalLiabilitiesId = resolveMetricId(metrics, ['Total Liabilities'], [
    'balance_sheet_total_liabilities',
  ]);
  const commonStockId = resolveMetricId(metrics, ['Common Stock'], ['balance_sheet_common_stock']);
  const retainedEarningsId = resolveMetricId(metrics, ['Retained Earnings'], ['balance_sheet_retained_earnings']);
  const treasuryStockId = resolveMetricId(metrics, ['Treasury Stock'], ['balance_sheet_treasury_stock']);
  const preferredStockId = resolveMetricId(metrics, ['Preferred Stock'], ['balance_sheet_preferred_stock']);
  const additionalPaidInCapitalId = resolveMetricId(metrics, ['Additional Paid In Capital'], [
    'balance_sheet_additional_paid_in_capital',
  ]);
  const aociId = resolveMetricId(metrics, ['Accumulated Other Income (Loss)'], [
    'balance_sheet_accumulated_other_comprehensive_income',
  ]);
  const otherStockholdersEquityId = resolveMetricId(metrics, ['Other Stock Holders Equity'], [
    'balance_sheet_other_total_stockholders_equity',
    'balance_sheet_other_total_shareholders_equity',
  ]);
  const totalEquityId = resolveMetricId(
    metrics,
    ['Total Share Holders Equity', 'Total Stockholders Equity', 'Total Shareholders Equity'],
    ['balance_sheet_total_common_shareholders_equity', 'balance_sheet_total_shareholders_equity']
  );
  const totalLiabilitiesAndEquityId = resolveMetricId(metrics, ['Total Liabilities And Total Equity'], [
    'balance_sheet_total_liabilities_and_shareholders_equity',
  ]);

  const rows: BalanceSheet[] = payload.data.map((row) => {
    const periodEndDate = resolveFiscalPeriodEndDate(row);
    const filingDate = resolveFiscalFilingDate(row);
    const date = periodEndDate || filingDate;
    const values = row.metricsValues ?? {};
    const fiscalYear = date ? String(new Date(date).getUTCFullYear()) : '';

    const get = (id: string | null) => (id ? parseMetricNumber(values[id]) : undefined);

    const bs = emptyBalanceSheet({
      date,
      symbol,
      reportedCurrency: 'USD',
      fiscalYear,
      filingDate: filingDate || '',
      period: String(row.periodType || '').toLowerCase().includes('annual') ? 'FY' : 'Q',
    });

    const apply = (key: BalanceSheetNumberKey, value: number | undefined) => {
      if (Number.isFinite(value)) bs[key] = value as number;
    };

    const rawCash = get(cashId);
    if (typeof rawCash === 'number') {
      const normalizedCash = rawCash < 1_000_000 ? rawCash * 1_000_000 : rawCash;
      bs.cashAndCashEquivalents = normalizedCash;
    }
    apply('shortTermInvestments', get(shortTermInvestmentsId));
    apply('cashAndShortTermInvestments', get(cashAndShortTermId));
    apply('netReceivables', get(receivablesId));
    apply('inventory', get(inventoryId));
    apply('otherCurrentAssets', get(otherCurrentAssetsId));
    apply('totalCurrentAssets', get(totalCurrentAssetsId));
    apply('propertyPlantEquipmentNet', get(ppeId));
    apply('goodwill', get(goodwillId));
    apply('intangibleAssets', get(intangiblesId));
    apply('longTermInvestments', get(longTermInvestmentsId));
    apply('otherNonCurrentAssets', get(otherLongTermAssetsId));
    apply('totalAssets', get(totalAssetsId));
    apply('accountPayables', get(accountsPayableId));
    apply('accruedExpenses', get(accruedExpensesId));
    apply('shortTermDebt', get(shortTermDebtId));
    apply('taxPayables', get(taxPayablesId));
    apply('deferredRevenue', get(deferredRevenueId));
    apply('otherCurrentLiabilities', get(otherCurrentLiabilitiesId));
    apply('totalCurrentLiabilities', get(totalCurrentLiabilitiesId));
    apply('longTermDebt', get(longTermDebtId));
    const leaseValue = get(leasesId);
    apply('capitalLeaseObligations', leaseValue);
    apply('capitalLeaseObligationsNonCurrent', leaseValue);
    apply('deferredRevenueNonCurrent', get(deferredRevenueNonCurrentId));
    apply('deferredTaxLiabilitiesNonCurrent', get(deferredTaxLiabilitiesNonCurrentId));
    apply('otherNonCurrentLiabilities', get(otherLongTermLiabilitiesId));
    apply('totalNonCurrentLiabilities', get(totalLongTermLiabilitiesId));
    apply('totalLiabilities', get(totalLiabilitiesId));
    apply('treasuryStock', get(treasuryStockId));
    apply('preferredStock', get(preferredStockId));
    apply('commonStock', get(commonStockId));
    apply('retainedEarnings', get(retainedEarningsId));
    apply('additionalPaidInCapital', get(additionalPaidInCapitalId));
    apply('accumulatedOtherComprehensiveIncomeLoss', get(aociId));
    apply('otherTotalStockholdersEquity', get(otherStockholdersEquityId));
    const totalEquity = get(totalEquityId);
    apply('totalStockholdersEquity', totalEquity);
    apply('totalEquity', totalEquity);
    const totalLiabilitiesAndEquity = get(totalLiabilitiesAndEquityId);
    apply('totalLiabilitiesAndTotalEquity', totalLiabilitiesAndEquity);
    apply('totalLiabilitiesAndStockholdersEquity', totalLiabilitiesAndEquity);

    return bs;
  });

  return { ...splitAnnualQuarterBalance(rows, payload.data), error: response.error };
}

export async function fetchFiscalStandardizedBalanceSheetTtm(
  symbol: string,
  metricCatalog?: FiscalStandardizedMetric[],
  currency?: string
): Promise<FiscalTtmResult<BalanceSheet>> {
  const companyKey = buildCompanyKeyFromList(symbol);
  if (!companyKey) return { row: null, error: 'Missing company key.' };

  const response = await fiscalGetWithError<FiscalStandardizedResponse>(
    '/v1/company/financials/balance-sheet/standardized',
    { companyKey, periodType: 'ltm', currency }
  );

  const payload = normalizeStandardizedPayload(response.data);
  if (!payload.data.length) return { row: null, error: response.error };
  const metrics = payload.metrics ?? metricCatalog ?? [];

  const cashId = resolveMetricId(metrics, ['Cash And Cash Equivalents', 'Cash & Equivalents'], [
    'balance_sheet_cash_and_cash_equivalents',
  ]);
  const shortTermInvestmentsId = resolveMetricId(metrics, [], ['balance_sheet_short_term_investments']);
  const cashAndShortTermId = resolveMetricId(metrics, ['Cash and Short Term Investments'], [
    'balance_sheet_total_cash_and_cash_equivalents',
  ]);
  const receivablesId = resolveMetricId(metrics, ['Receivables', 'Accounts Receivable'], [
    'balance_sheet_accounts_receivable',
  ]);
  const inventoryId = resolveMetricId(metrics, [], ['balance_sheet_inventories']);
  const otherCurrentAssetsId = resolveMetricId(metrics, ['Other Current Assets'], [
    'balance_sheet_other_current_assets',
  ]);
  const totalCurrentAssetsId = resolveMetricId(metrics, ['Total Current Assets'], [
    'balance_sheet_total_current_assets',
  ]);
  const ppeId = resolveMetricId(metrics, ['Property, Plant and Equipment', 'Property, Plant & Equipment'], [
    'balance_sheet_net_property_plant_and_equipment',
  ]);
  const goodwillId = resolveMetricId(metrics, ['Goodwill'], ['balance_sheet_goodwill']);
  const intangiblesId = resolveMetricId(metrics, ['Intangible Assets'], ['balance_sheet_net_intangible_assets']);
  const longTermInvestmentsId = resolveMetricId(metrics, ['Long Term Investments'], [
    'balance_sheet_long_term_investments',
  ]);
  const otherLongTermAssetsId = resolveMetricId(metrics, ['Other Long Term Assets'], [
    'balance_sheet_other_long_term_assets',
  ]);
  const totalAssetsId = resolveMetricId(metrics, ['Total Assets'], ['balance_sheet_total_assets']);
  const accountsPayableId = resolveMetricId(metrics, ['Accounts Payable'], [
    'balance_sheet_accounts_payable',
  ]);
  const accruedExpensesId = resolveMetricId(metrics, ['Accrued Expenses'], [
    'balance_sheet_accrued_expenses',
  ]);
  const shortTermDebtId = resolveMetricId(metrics, [], ['balance_sheet_short_term_debt']);
  const taxPayablesId = resolveMetricId(metrics, ['Tax Payables', 'Income Taxes Payable'], [
    'balance_sheet_tax_payable',
  ]);
  const deferredRevenueId = resolveMetricId(metrics, ['Deferred Revenue', 'Unearned Revenue'], [
    'balance_sheet_unearned_revenue',
  ]);
  const otherCurrentLiabilitiesId = resolveMetricId(metrics, ['Other Current Liabilities'], [
    'balance_sheet_other_current_liabilities',
  ]);
  const totalCurrentLiabilitiesId = resolveMetricId(metrics, ['Total Current Liabilities'], [
    'balance_sheet_total_current_liabilities',
  ]);
  const longTermDebtId = resolveMetricId(metrics, ['Total Long Term Debt', 'Long Term Debt'], [
    'balance_sheet_long_term_debt',
  ]);
  const leasesId = resolveMetricId(metrics, ['Capital Lease Obligations', 'Leases'], [
    'balance_sheet_leases',
  ]);
  const deferredRevenueNonCurrentId = resolveMetricId(metrics, ['Deferred Revenue Non-Current'], [
    'balance_sheet_deferred_revenue_non_current',
  ]);
  const deferredTaxLiabilitiesNonCurrentId = resolveMetricId(metrics, ['Deferred Tax Liabilities Non-Current'], [
    'balance_sheet_deferred_tax_liabilities_non_current',
  ]);
  const otherLongTermLiabilitiesId = resolveMetricId(metrics, ['Other Long Term Liabilities'], [
    'balance_sheet_other_long_term_liabilities',
  ]);
  const totalLongTermLiabilitiesId = resolveMetricId(metrics, ['Total Long-Term Liabilities'], [
    'balance_sheet_total_long_term_liabilities',
  ]);
  const totalLiabilitiesId = resolveMetricId(metrics, ['Total Liabilities'], [
    'balance_sheet_total_liabilities',
  ]);
  const commonStockId = resolveMetricId(metrics, ['Common Stock'], ['balance_sheet_common_stock']);
  const retainedEarningsId = resolveMetricId(metrics, ['Retained Earnings'], ['balance_sheet_retained_earnings']);
  const treasuryStockId = resolveMetricId(metrics, ['Treasury Stock'], ['balance_sheet_treasury_stock']);
  const preferredStockId = resolveMetricId(metrics, ['Preferred Stock'], ['balance_sheet_preferred_stock']);
  const additionalPaidInCapitalId = resolveMetricId(metrics, ['Additional Paid In Capital'], [
    'balance_sheet_additional_paid_in_capital',
  ]);
  const aociId = resolveMetricId(metrics, ['Accumulated Other Income (Loss)'], [
    'balance_sheet_accumulated_other_comprehensive_income',
  ]);
  const otherStockholdersEquityId = resolveMetricId(metrics, ['Other Stock Holders Equity'], [
    'balance_sheet_other_total_stockholders_equity',
    'balance_sheet_other_total_shareholders_equity',
  ]);
  const totalEquityId = resolveMetricId(
    metrics,
    ['Total Share Holders Equity', 'Total Stockholders Equity', 'Total Shareholders Equity'],
    ['balance_sheet_total_common_shareholders_equity', 'balance_sheet_total_shareholders_equity']
  );
  const totalLiabilitiesAndEquityId = resolveMetricId(metrics, ['Total Liabilities And Total Equity'], [
    'balance_sheet_total_liabilities_and_shareholders_equity',
  ]);

  const first = payload.data[0];
  const values = first.metricsValues ?? {};
  const get = (id: string | null) => (id ? parseMetricNumber(values[id]) : undefined);

  const periodEndDate = resolveFiscalPeriodEndDate(first);
  const filingDate = resolveFiscalFilingDate(first);
  const ttmDate = periodEndDate || filingDate;
  const row = emptyBalanceSheet({
    date: ttmDate,
    symbol,
    reportedCurrency: 'USD',
    fiscalYear: ttmDate ? String(new Date(ttmDate).getUTCFullYear()) : '',
    filingDate: filingDate || '',
    period: 'TTM',
  });

  const apply = (key: BalanceSheetNumberKey, value: number | undefined) => {
    if (Number.isFinite(value)) row[key] = value as number;
  };

  apply('cashAndCashEquivalents', get(cashId));
  apply('shortTermInvestments', get(shortTermInvestmentsId));
  apply('cashAndShortTermInvestments', get(cashAndShortTermId));
  apply('netReceivables', get(receivablesId));
  apply('inventory', get(inventoryId));
  apply('otherCurrentAssets', get(otherCurrentAssetsId));
  apply('totalCurrentAssets', get(totalCurrentAssetsId));
  apply('propertyPlantEquipmentNet', get(ppeId));
  apply('goodwill', get(goodwillId));
  apply('intangibleAssets', get(intangiblesId));
  apply('longTermInvestments', get(longTermInvestmentsId));
  apply('otherNonCurrentAssets', get(otherLongTermAssetsId));
  apply('totalAssets', get(totalAssetsId));
  apply('accountPayables', get(accountsPayableId));
  apply('accruedExpenses', get(accruedExpensesId));
  apply('shortTermDebt', get(shortTermDebtId));
  apply('taxPayables', get(taxPayablesId));
  apply('deferredRevenue', get(deferredRevenueId));
  apply('otherCurrentLiabilities', get(otherCurrentLiabilitiesId));
  apply('totalCurrentLiabilities', get(totalCurrentLiabilitiesId));
  apply('longTermDebt', get(longTermDebtId));
  const leaseValue = get(leasesId);
  apply('capitalLeaseObligations', leaseValue);
  apply('capitalLeaseObligationsNonCurrent', leaseValue);
  apply('deferredRevenueNonCurrent', get(deferredRevenueNonCurrentId));
  apply('deferredTaxLiabilitiesNonCurrent', get(deferredTaxLiabilitiesNonCurrentId));
  apply('otherNonCurrentLiabilities', get(otherLongTermLiabilitiesId));
  apply('totalNonCurrentLiabilities', get(totalLongTermLiabilitiesId));
  apply('totalLiabilities', get(totalLiabilitiesId));
  apply('treasuryStock', get(treasuryStockId));
  apply('preferredStock', get(preferredStockId));
  apply('commonStock', get(commonStockId));
  apply('retainedEarnings', get(retainedEarningsId));
  apply('additionalPaidInCapital', get(additionalPaidInCapitalId));
  apply('accumulatedOtherComprehensiveIncomeLoss', get(aociId));
  apply('otherTotalStockholdersEquity', get(otherStockholdersEquityId));
  const totalEquity = get(totalEquityId);
  apply('totalStockholdersEquity', totalEquity);
  apply('totalEquity', totalEquity);
  const totalLiabilitiesAndEquity = get(totalLiabilitiesAndEquityId);
  apply('totalLiabilitiesAndTotalEquity', totalLiabilitiesAndEquity);
  apply('totalLiabilitiesAndStockholdersEquity', totalLiabilitiesAndEquity);

  return { row, date: row.date, error: response.error };
}
